import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal, totalQty, coupon, setCoupon } = useCart();
  const navigate = useNavigate();

  const couponCode = 'LEILA200';
  const couponDiscount = 200;
  const couponMin = 2;
  const couponValid = coupon.trim().toUpperCase() === couponCode && totalQty >= couponMin;
  const discount = couponValid ? couponDiscount : 0;
  const total = Math.max(subtotal - discount, 0);

  return (
    <div className="min-h-screen bg-[#f7f2ec] pt-8 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* header */}
        <div className="flex items-center justify-between py-6 border-b border-stone-200">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/images/leila-logo.jpg"
              alt="Leila Tesettür"
              className="h-14 w-14 rounded-full object-cover ring-1 ring-stone-200"
            />
            <span className="text-[10px] tracking-[0.3em] uppercase text-stone-500">Sepet</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-stone-700 hover:text-stone-950 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Alışverişe Devam
          </Link>
        </div>

        <h1 className="mt-10 font-serif text-4xl lg:text-6xl text-stone-900">
          Sepetiniz <span className="text-stone-400 font-light">({totalQty} ürün)</span>
        </h1>

        {items.length === 0 ? (
          <div className="mt-16 max-w-lg">
            <p className="text-stone-600">Sepetinizde ürün bulunmuyor.</p>
            <Link
              to="/#koleksiyon"
              className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 bg-stone-900 text-[#f7f2ec] text-[11px] tracking-[0.24em] uppercase hover:bg-stone-800 transition-colors"
            >
              <ShoppingBag className="h-4 w-4" /> Koleksiyona Göz At
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid lg:grid-cols-3 gap-10">
            {/* items */}
            <div className="lg:col-span-2 space-y-4">
              {items.map((it) => (
                <div key={it.key} className="bg-white/60 border border-stone-200 p-4 flex gap-4">
                  <img src={it.image} alt={it.name} className="w-24 h-32 object-cover bg-stone-100 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-serif text-xl text-stone-900">{it.name}</h3>
                        <p className="mt-1 text-[12px] text-stone-500">
                          Beden {it.size} · Renk {it.color}
                        </p>
                      </div>
                      <p className="font-serif text-lg text-stone-900 whitespace-nowrap">
                        ₺{(it.price * it.qty).toLocaleString('tr-TR')}
                      </p>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-4">
                      <div className="inline-flex items-center border border-stone-300">
                        <button onClick={() => updateQty(it.key, it.qty - 1)} className="h-9 w-9 hover:bg-stone-100" aria-label="Azalt">
                          <Minus className="h-3.5 w-3.5 mx-auto" />
                        </button>
                        <span className="w-10 text-center text-sm">{it.qty}</span>
                        <button onClick={() => updateQty(it.key, it.qty + 1)} className="h-9 w-9 hover:bg-stone-100" aria-label="Artır">
                          <Plus className="h-3.5 w-3.5 mx-auto" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(it.key)}
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-stone-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Sil
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* summary */}
            <aside className="bg-white/60 border border-stone-200 p-6 h-fit sticky top-24">
              <h2 className="font-serif text-2xl text-stone-900">Özet</h2>

              <div className="mt-5">
                <label className="text-[10px] tracking-[0.28em] uppercase text-stone-500">Kupon Kodu</label>
                <div className="mt-2 flex gap-2">
                  <input
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value)}
                    placeholder="Örn: LEILA200"
                    className="flex-1 bg-transparent border border-stone-300 focus:border-stone-900 outline-none px-3 py-2.5 text-stone-900 placeholder:text-stone-400 uppercase tracking-wider text-sm"
                  />
                </div>
                {coupon && !couponValid && (
                  <p className="mt-2 text-[12px] text-stone-500">
                    Geçerli değil. `LEILA200` kodu 2 veya daha fazla üründe geçerlidir.
                  </p>
                )}
                {couponValid && (
                  <p className="mt-2 text-[12px] text-green-800">Kupon uygulandı: -₺{couponDiscount}</p>
                )}
              </div>

              <div className="mt-6 space-y-2 text-[14px] pt-6 border-t border-stone-200">
                <div className="flex justify-between">
                  <span className="text-stone-600">Ara Toplam</span>
                  <span className="text-stone-900">₺{subtotal.toLocaleString('tr-TR')}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-stone-600">İndirim</span>
                    <span className="text-green-800">-₺{discount.toLocaleString('tr-TR')}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-stone-600">Kargo</span>
                  <span className="text-stone-900">Ücretsiz</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-stone-200 flex items-baseline justify-between">
                <span className="text-[11px] tracking-[0.28em] uppercase text-stone-500">Toplam</span>
                <span className="font-serif text-3xl text-stone-900">₺{total.toLocaleString('tr-TR')}</span>
              </div>

              <button
                onClick={() => navigate('/odeme')}
                className="mt-6 w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-stone-900 text-[#f7f2ec] text-[11px] tracking-[0.24em] uppercase hover:bg-stone-800 transition-colors"
              >
                Sipariş Ver
              </button>
              <p className="mt-3 text-[12px] text-stone-500 text-center">
                Sipariş Ver'e tıkladığınızda <span className="text-stone-800">Havale/EFT</span> ödeme sayfasına yönlendirileceksiniz.
              </p>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
